import type { CommonColors } from '@mui/material/styles';

import { Colors } from './colors';

import type { ThemeDirection, ThemeColorScheme, ThemeCssVariables } from './types';

// ----------------------------------------------------------------------

// Extended palette color type that includes lighter and darker shades
type ExtendedPaletteColor = {
  lighter: string;
  light: string;
  main: string;
  dark: string;
  darker: string;
  contrastText: string;
};

type ThemeConfig = {
  classesPrefix: string;
  modeStorageKey: string;
  direction: ThemeDirection;
  defaultMode: ThemeColorScheme;
  cssVariables: ThemeCssVariables;
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error',
    ExtendedPaletteColor
  > & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: Record<
      '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900',
      string
    >;
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  direction: 'ltr',
  defaultMode: 'light',
  modeStorageKey: 'theme-mode',
  classesPrefix: 'minimal',
  /** **************************************
   * Typography
   *************************************** */
  fontFamily: {
    primary: 'Satoshi',
    // primary: 'Public Sans Variable',
    secondary: 'Barlow',
  },
  /** **************************************
   * Palette
   *************************************** */
  palette: {
    primary: {
      lighter: '#B8F5E0',
      light: '#6CF2C2',
      main: '#2FB7A0',
      dark: '#1F4ED8',
      darker: '#2F2F33',
      contrastText: '#FFFFFF',
    },
    secondary: {
      lighter: '#C5D5F5',
      light: '#6B8AE8',
      main: '#1F4ED8',
      dark: '#1638A0',
      darker: '#0F2670',
      contrastText: '#FFFFFF',
    },
    info: {
      lighter: '#CAFDF5',

      light: Colors.smoke400,
      main: Colors.smoke800,
      dark: Colors.smoke900,
      darker: '#003768',
      contrastText: '#FFFFFF',
    },
    success: {
      lighter: '#D3FCD2',

      light: Colors.success500,
      main: Colors.success700,
      dark: Colors.success800,
      darker: '#065E49',
      contrastText: '#ffffff',
    },
    warning: {
      lighter: '#FFF5CC',

      light: Colors.warning300,
      main: Colors.warning500,
      dark: Colors.warning700,
      darker: '#7A4100',
      contrastText: '#1C252E',
    },
    error: {
      lighter: '#FFE9D5',

      light: Colors.error300,
      main: Colors.error500,
      dark: Colors.error700,
      darker: '#7A0916',
      contrastText: '#FFFFFF',
    },
    grey: {
      '50': '#FCFDFD',
      '100': '#F9FAFB',
      '200': '#F4F6F8',
      '300': '#DFE3E8',
      '400': '#C4CDD5',
      '500': '#919EAB',
      '600': '#637381',
      '700': '#454F5B',
      '800': '#2F2F33',
      '900': '#1A1A1D',
    },
    common: { black: '#000000', white: '#FFFFFF' },
  },
  /** **************************************
   * Css variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },
};
